import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Heart, Activity, CheckCircle, HelpCircle, ArrowRight, Shield, Clock } from 'react-feather';

const BloodPressureCheck = () => {
  const navigate = useNavigate();
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative h-[60vh] flex items-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1621525434111-87a99d170b0e?q=80&w=1170&auto=format&fit=crop"
            alt="Blood Pressure Check"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-blue-900/90 to-blue-600/40" />
        </div>

        <div className="container mx-auto px-6 relative z-10">
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-2xl text-white"
          >
            <div className="inline-flex items-center space-x-2 bg-blue-500/20 backdrop-blur-md border border-blue-400/30 rounded-full px-4 py-2 mb-6">
              <Activity className="h-4 w-4 text-blue-200" />
              <span className="text-sm font-semibold tracking-wide uppercase">Cardiovascular Health</span>
            </div>
            <h1 className="text-5xl md:text-7xl font-bold mb-6 leading-tight">
              Blood Pressure <br />
              <span className="text-blue-200">Screening</span>
            </h1>
            <p className="text-xl text-blue-50 mb-8 leading-relaxed">
              Quick, accurate measurements and expert advice to help you maintain a healthy heart and prevent hypertension.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-20 -mt-20 relative z-20">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            
            {/* Left Column: Info & Importance */}
            <div className="lg:col-span-8">
              <motion.div
                variants={containerVariants}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                className="space-y-12"
              >
                {/* Intro Card */}
                <motion.div variants={itemVariants} className="bg-white p-8 md:p-12 rounded-3xl shadow-xl border border-gray-100">
                  <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center">
                    <Heart className="mr-4 text-blue-500" />
                    Benefits of Regular Checks
                  </h2>
                  <p className="text-lg text-gray-600 leading-relaxed mb-8">
                    Regular blood pressure checks are essential for maintaining heart health and preventing serious conditions like hypertension and stroke. 
                    Early detection allows for timely intervention and better long-term outcomes.
                  </p>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {[
                      "Early detection of hypertension",
                      "Monitor lifestyle effectiveness",
                      "Reduce heart disease risk",
                      "Peace of mind for your heart"
                    ].map((benefit, index) => (
                      <div key={index} className="flex items-start p-4 bg-blue-50 rounded-2xl border border-blue-100">
                        <CheckCircle className="h-6 w-6 text-blue-500 mr-3 flex-shrink-0" />
                        <span className="font-semibold text-gray-800">{benefit}</span>
                      </div>
                    ))}
                  </div>
                </motion.div>

                {/* What to Expect */}
                <motion.div variants={itemVariants} className="bg-gradient-to-br from-blue-600 to-blue-700 p-8 md:p-12 rounded-3xl shadow-xl text-white relative overflow-hidden">
                  <div className="absolute right-0 bottom-0 opacity-10">
                    <Activity size={200} />
                  </div>
                  <div className="relative z-10">
                    <h2 className="text-3xl font-bold mb-6">What to Expect</h2>
                    <p className="text-xl text-blue-50 leading-relaxed mb-8">
                      Our trained staff will take your blood pressure using advanced, accurate equipment. 
                      You’ll receive immediate feedback and personalized advice on diet, exercise, and lifestyle changes.
                    </p>
                    <div className="flex flex-wrap gap-4">
                      <div className="flex items-center bg-white/10 backdrop-blur-md rounded-full px-5 py-2">
                        <Shield className="h-5 w-5 mr-2" />
                        <span>Advanced Equipment</span>
                      </div>
                      <div className="flex items-center bg-white/10 backdrop-blur-md rounded-full px-5 py-2">
                        <Clock className="h-5 w-5 mr-2" />
                        <span>Quick Assessment</span>
                      </div>
                    </div>
                  </div>
                </motion.div>

                {/* FAQ Section */}
                <motion.div variants={itemVariants} className="space-y-6">
                  <h2 className="text-3xl font-bold text-gray-900 mb-8 flex items-center">
                    <HelpCircle className="mr-4 text-blue-500" />
                    Common Questions
                  </h2>
                  <div className="grid gap-6">
                    {[
                      {
                        q: "How often should I get my blood pressure checked?",
                        a: "It's recommended to check your blood pressure at least once a year if you’re at normal risk, and more frequently if you have a history of hypertension or other risk factors."
                      },
                      {
                        q: "Can lifestyle changes help lower my blood pressure?",
                        a: "Yes, lifestyle changes such as a balanced diet (like the DASH diet), regular exercise, stress management, and limiting alcohol can significantly impact your blood pressure levels."
                      }
                    ].map((faq, index) => (
                      <div key={index} className="group bg-gray-50 hover:bg-white p-6 rounded-2xl border border-gray-100 hover:shadow-lg transition-all duration-300">
                        <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-blue-600 transition-colors">Q: {faq.q}</h3>
                        <p className="text-gray-600 leading-relaxed">A: {faq.a}</p>
                      </div>
                    ))}
                  </div>
                </motion.div>
              </motion.div>
            </div>

            {/* Right Column: CTA / Sticky Info */}
            <div className="lg:col-span-4">
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                className="sticky top-24"
              >
                <div className="bg-gray-900 rounded-3xl p-8 text-white shadow-2xl relative overflow-hidden">
                  <div className="absolute top-0 right-0 w-32 h-32 bg-blue-500/20 blur-3xl -mr-16 -mt-16 rounded-full" />
                  <h3 className="text-2xl font-bold mb-4 relative z-10">Start your heart health journey</h3>
                  <p className="text-gray-400 mb-8 relative z-10">
                    Visit us today for a professional blood pressure check. No appointment needed.
                  </p>
                  <button 
                    onClick={() => navigate('/about-us')}
                    className="w-full bg-blue-500 hover:bg-blue-600 text-white font-bold py-4 rounded-2xl transition-all duration-300 flex items-center justify-center group"
                  >
                    Visit Our Pharmacy
                    <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" />
                  </button>
                  
                  <div className="mt-8 pt-8 border-t border-gray-800 space-y-4">
                    <div className="flex items-center text-gray-300">
                      <div className="w-10 h-10 bg-gray-800 rounded-xl flex items-center justify-center mr-4">
                        <Heart className="h-5 w-5 text-blue-400" />
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 uppercase font-bold tracking-wider">Heart Care</p>
                        <p className="font-semibold">Preventative Screening</p>
                      </div>
                    </div>
                    <div className="flex items-center text-gray-300">
                      <div className="w-10 h-10 bg-gray-800 rounded-xl flex items-center justify-center mr-4">
                        <Shield className="h-5 w-5 text-blue-400" />
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 uppercase font-bold tracking-wider">Accurate</p>
                        <p className="font-semibold">Clinical Grade Tools</p>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>

          </div>
        </div>
      </section>
    </div>
  );
};

export default BloodPressureCheck;
